import { ScopeOfPracticeEnum } from '@umss/shared/utils';

/**
 * Schedule-derived status of a contractor for the selected shift needs, shown on the
 * Assign Contractor modal. Computed independently of any pending shift-request state
 * (request state is a separate overlay that drives the Send button / badge).
 *
 * Precedence when aggregating across multiple selected shift needs is
 * Booked > Unavailable > Available:
 *   - Booked if the contractor has an accepted (actual) shift conflicting with ANY need,
 *   - else Unavailable if they are outside their scheduled hours (or have no availability
 *     set at all) for ANY need,
 *   - else Available.
 */
export enum ContractorAvailabilityStatus {
  Available = 'Available',
  Unavailable = 'Unavailable',
  Booked = 'Booked'
}

// DTO for a contractor shown in the Assign Contractor modal
export interface AvailableContractorForShiftsDto {
  contractorId: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phoneNumber: string | null;
  profilePictureUrl: string | null;
  scopeOfPractice: ScopeOfPracticeEnum;
  scopeOfPracticeDisplay: string;
  hourlyRate: number | null;
  distanceMiles: number | null;
  isFavorite: boolean;
  // Schedule-derived status, aggregated across all selected shift needs.
  // Independent of the request-state flags below.
  availabilityStatus: ContractorAvailabilityStatus;
  availabilityStatusDisplay: string;
  averageRating: number | null;
  reviewCount: number;
  city: string | null;
  stateCode: string | null;
  // Drives a "Request sent" badge and disables Send; does not affect availabilityStatus.
  hasFacilityRequest: boolean;
  // Drives an "Applied" badge and disables Send (approval stays in the existing approvals
  // flow); does not affect availabilityStatus.
  hasContractorRequest: boolean;
}

// Filter/paging options for the Assign Contractor modal.
// No scope filter — the list is always a single scope (the shift needs' scope).
export interface AvailableContractorsFilterDto {
  shiftNeedIds: string[];
  // Restrict to a single availability status. Defaults to Available on the backend;
  // pass null explicitly to return all statuses.
  availabilityStatus?: ContractorAvailabilityStatus | null;
  isFavorite?: boolean | null;
  stateCode?: string | null;
  maxDistanceMiles?: number | null;
  searchTerm?: string | null;
  pageNumber?: number;
  pageSize?: number;
}

// Paged response for available contractors. Every contractor of the shift needs' scope is
// returned (subject to the filters), each tagged with an availability status.
export interface AvailableContractorsForShiftsResponseDto {
  contractors: AvailableContractorForShiftsDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}
